'use client'

import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Turnstile } from 'next-turnstile'
import { v4 as uuidv4 } from 'uuid'

export type TurnstileStatus = 'success' | 'error' | 'expired' | 'required'

export interface TurnstileCaptchaProps {
  onVerify?: (token: string, idempotencyKey: string) => void
  onError?: () => void
  onExpire?: () => void
  className?: string
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact'
}

export interface TurnstileCaptchaRef {
  reset: () => void
}

const TurnstileCaptcha = forwardRef<TurnstileCaptchaRef, TurnstileCaptchaProps>(
  ({ onVerify, onError, onExpire, className = '', theme = 'auto', size = 'normal' }, ref) => {
    const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>('required')
    const [siteKey, setSiteKey] = useState<string>('')
    const [isLoading, setIsLoading] = useState(true)
    // Bumped by `reset()` to force a brand-new widget (see useImperativeHandle below).
    const [widgetNonce, setWidgetNonce] = useState(0)
    const turnstileRef = useRef<string>('')
    const widgetId = useRef<string>(`turnstile-${uuidv4()}`).current // Unique widget ID
    const idempotencyKey = useRef(uuidv4()).current // Generate once per component instance

    useEffect(() => {
      async function fetchConfig() {
        try {
          const response = await fetch('/api/config')
          const data = await response.json()
          setSiteKey(data.turnstileSiteKey)
        } catch (error) {
          console.error('Failed to load Turnstile configuration:', error)
        } finally {
          setIsLoading(false)
        }
      }

      fetchConfig()
    }, [])

    const handleSuccess = (token: string) => {
      turnstileRef.current = token
      setTurnstileStatus('success')
      if (onVerify) {
        onVerify(token, idempotencyKey)
      }
    }

    const handleError = () => {
      setTurnstileStatus('error')
      if (onError) {
        onError()
      }
    }

    const handleExpire = () => {
      setTurnstileStatus('expired')
      if (onExpire) {
        onExpire()
      }
    }

    // Expose reset method to parent components via ref.
    //
    // A Turnstile token is single-use: once a login attempt consumes it, the caller MUST get a
    // fresh one or its submit button stays disabled forever (issue #3973). We remount the widget
    // instead of calling `window.turnstile.reset()` because the opaque widget id returned by
    // `turnstile.render()` is owned by next-turnstile and never exposed — our `widgetId` is only a
    // DOM `id` attribute, so `turnstile.reset(widgetId)` never matched a widget and silently
    // failed. Remounting makes next-turnstile remove the old widget and render a new challenge,
    // which fires `onVerify` again with a fresh token.
    useImperativeHandle(ref, () => ({
      reset: () => {
        turnstileRef.current = ''
        setTurnstileStatus('required')
        setWidgetNonce((nonce) => nonce + 1)
      },
    }))

    if (isLoading || !siteKey) {
      return <div className={className}>טוען בדיקת אבטחה...</div>
    }

    return (
      <div className={`flex justify-center w-full ${className}`}>
        <Turnstile
          key={widgetNonce}
          id={widgetId}
          siteKey={siteKey}
          onVerify={handleSuccess}
          onError={handleError}
          onExpire={handleExpire}
          theme={theme}
          size={size}
          language={'he'}
        />
      </div>
    )
  },
)

TurnstileCaptcha.displayName = 'TurnstileCaptcha'

export default TurnstileCaptcha
